import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { PurchaseOrders, PurchaseOrderLineItems, Materials, MaterialVendors } from "types/api-types";

interface RequisitionLineItem {
  materialId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  deliveryDate: string;
  notes: string;
  preferredVendorId: string;
}

interface PORequisitionData {
  requisitionNumber: string;
  requestedBy: string;
  department: string;
  jobId: string;
  priority: "low" | "medium" | "high" | "urgent";
  justification: string;
  lineItems: RequisitionLineItem[];
  approvalRequired: boolean;
  approvers: string[];
  deliveryLocation: string;
  budgetCode: string;
  expectedTotal: number;
  notes: string;
  status: "draft" | "submitted" | "approved" | "rejected" | "ordered";
}

class PORequisitionWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: PORequisitionData = {
    requisitionNumber: "",  // Will be initialized in constructor
    requestedBy: "",
    department: "",
    jobId: "",
    priority: "medium",
    justification: "",
    lineItems: [],
    approvalRequired: true,
    approvers: [],
    deliveryLocation: "",
    budgetCode: "",
    expectedTotal: 0,
    notes: "",
    status: "draft",
  };

  // Lookup Data
  materials: Materials[] = [];
  vendors: MaterialVendors[] = [];
  departments: any[] = [];
  jobs: any[] = [];
  budgetCodes: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    // Initialize requisition number after makeAutoObservable
    this.formData.requisitionNumber = this.generateRequisitionNumber();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = <K extends keyof PORequisitionData>(
    field: K,
    value: PORequisitionData[K]
  ) => {
    this.formData[field] = value;
    if (field === "lineItems") {
      this.calculateTotal();
    }
  };

  addLineItem = (item: RequisitionLineItem) => {
    this.formData.lineItems.push(item);
    this.calculateTotal();
  };

  removeLineItem = (index: number) => {
    this.formData.lineItems.splice(index, 1);
    this.calculateTotal();
  };

  updateLineItem = (index: number, item: RequisitionLineItem) => {
    this.formData.lineItems[index] = item;
    this.calculateTotal();
  };

  addApprover = (userId: string) => {
    if (!this.formData.approvers.includes(userId)) {
      this.formData.approvers.push(userId);
    }
  };

  removeApprover = (userId: string) => {
    const index = this.formData.approvers.indexOf(userId);
    if (index > -1) {
      this.formData.approvers.splice(index, 1);
    }
  };

  calculateTotal = () => {
    const total = this.formData.lineItems.reduce(
      (sum, item) => sum + item.totalPrice,
      0
    );
    this.formData.expectedTotal = total;
  };

  generateRequisitionNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `REQ-${year}${month}${day}-${random}`;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.requestedBy) {
          this.error = "Please specify who is requesting this purchase order";
          return false;
        }
        if (!this.formData.department) {
          this.error = "Please select a department";
          return false;
        }
        if (!this.formData.justification) {
          this.error = "Please provide justification for this purchase";
          return false;
        }
        return true;

      case 1:
        if (this.formData.lineItems.length === 0) {
          this.error = "Please add at least one line item";
          return false;
        }

        const invalidItems = this.formData.lineItems.some(
          (item) => !item.materialId || !item.description || item.quantity <= 0 || item.unitPrice <= 0
        );
        if (invalidItems) {
          this.error = "Please complete all line item details";
          return false;
        }
        return true;

      case 2:
        if (this.formData.approvalRequired && this.formData.approvers.length === 0) {
          this.error = "Please select at least one approver";
          return false;
        }
        if (!this.formData.deliveryLocation) {
          this.error = "Please specify delivery location";
          return false;
        }
        if (!this.formData.budgetCode) {
          this.error = "Please select a budget code";
          return false;
        }
        return true;

      case 3:
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load materials
      if (this.rootStore.materialsStore) {
        await this.rootStore.materialsStore.find({
          query: {
            $limit: 500,
            active: true,
          },
        });
      }

      // Load vendors
      if (this.rootStore.materialVendorsStore) {
        await this.rootStore.materialVendorsStore.find({
          query: {
            $limit: 100,
            active: true,
          },
        });
      }

      // Load jobs if needed
      if (this.rootStore.jobStore) {
        await this.rootStore.jobStore.find({
          query: {
            $limit: 100,
            status: "active",
          },
        });
      }

      // Load departments
      if (this.rootStore.departmentsStore) {
        await this.rootStore.departmentsStore.find({
          query: {
            $limit: 100,
            active: true,
          },
        });
      }

      runInAction(() => {
        this.materials = this.rootStore.materialsStore?.entities || [];
        this.vendors = this.rootStore.materialVendorsStore?.entities || [];
        this.jobs = this.rootStore.jobStore?.entities || [];
        this.departments = this.rootStore.departmentsStore?.entities || [];

        // Mock budget codes (would come from API)
        this.budgetCodes = [
          { id: "1", code: "CAPEX-001", description: "Capital Equipment" },
          { id: "2", code: "OPEX-001", description: "Operating Expenses" },
          { id: "3", code: "MAINT-001", description: "Maintenance" },
          { id: "4", code: "RAW-001", description: "Raw Materials" },
          { id: "5", code: "SUPPLY-001", description: "Office Supplies" },
        ];

        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  submitRequisition = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      // Create requisition record
      const requisitionData = {
        ...this.formData,
        submittedAt: new Date().toISOString(),
        submittedBy: this.rootStore.authStore?.user?.id,
      };

      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulated API call

      runInAction(() => {
        this.success = "Purchase order requisition submitted successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Dashboard");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit requisition";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get users() {
    return this.rootStore.userStore?.entities || [];
  }

  get selectedJob() {
    return this.jobs.find((j) => j.id === this.formData.jobId);
  }

  get selectedDepartment() {
    return this.departments.find((d) => d.id === this.formData.department);
  }

  get selectedBudgetCode() {
    return this.budgetCodes.find((b) => b.id === this.formData.budgetCode);
  }

  get totalItems() {
    return this.formData.lineItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  get uniqueVendors() {
    const vendorIds = new Set(this.formData.lineItems.map(item => item.preferredVendorId).filter(Boolean));
    return Array.from(vendorIds).map(id => this.vendors.find(v => v.id === id)).filter(Boolean);
  }

  get requiresHighApproval() {
    return this.formData.expectedTotal > 10000 || this.formData.priority === "urgent";
  }

  get getMaterialById() {
    return (materialId: string) => this.materials.find((m) => m.id === materialId);
  }

  get getVendorById() {
    return (vendorId: string) => this.vendors.find((v) => v.id === vendorId);
  }

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = {
      requisitionNumber: this.generateRequisitionNumber(),
      requestedBy: "",
      department: "",
      jobId: "",
      priority: "medium",
      justification: "",
      lineItems: [],
      approvalRequired: true,
      approvers: [],
      deliveryLocation: "",
      budgetCode: "",
      expectedTotal: 0,
      notes: "",
      status: "draft",
    };
  };
}

export default PORequisitionWizardStore;